import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box, Card, CardContent, Switch, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Typography,
} from "@mui/material";
import { cardTitleSx, numberSx, tokens } from "../../theme";
import { roundToX } from "../../common/common";
import {
    setFocusEnabled, setFocusIterations, setFocusEliminatePercent, setFocusKeepBestPercent,
} from "../../reducer/RawSurfaceSlice";
import MiniField from "../cards/MiniField";

// Same table shape as Scan Analysis's own FocusRemoveWorst.js (src/components/cards/) - Machine/
// Motor rows, Initial/Y/Z/Total/% removed columns - different wiring only. Unlike that card, this
// one also gets an Enabled switch (see the plan this was built from: letting a stage be turned
// off entirely to see its effect on shim is the whole point here, not just tuning it).
const headerCellSx = {
    fontSize: "10.5px", fontWeight: 600, color: tokens.muted,
    textTransform: "uppercase", letterSpacing: "0.04em", border: "none", padding: "3px 4px",
    whiteSpace: "nowrap",
};
const groupHeaderCellSx = { ...headerCellSx, borderBottom: `1px solid ${tokens.line}` };
const rowHeaderCellSx = {
    fontSize: "12px", fontWeight: 600, color: tokens.textSoft, border: "none", padding: "3px 4px",
    whiteSpace: "nowrap",
};
const numCellSx = { ...numberSx, fontSize: "12px", border: "none", padding: "3px 4px" };
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const calcPercent = (zRemoved, yRemoved, initialPointCount) =>
    `${roundToX(((zRemoved + yRemoved) / initialPointCount) * 100, 1)}%`;

export default function FocusRemoveWorstCard({ commit }) {
    const rawSurface = useSelector((state) => state.rawSurface);
    const dispatch = useDispatch();

    const [iterationsText, setIterationsText] = useState(String(rawSurface.focusIterations));
    const [eliminateText, setEliminateText] = useState(String(Math.round(rawSurface.focusEliminatePercent * 100)));
    const [keepBestText, setKeepBestText] = useState(String(Math.round(rawSurface.focusKeepBestPercent * 100)));
    const [iterationError, setIterationError] = useState(false);
    const [eliminateError, setEliminateError] = useState(false);
    const [keepBestError, setKeepBestError] = useState(false);

    useEffect(() => setIterationsText(String(rawSurface.focusIterations)), [rawSurface.focusIterations]);
    useEffect(
        () => setEliminateText(String(Math.round(rawSurface.focusEliminatePercent * 100))),
        [rawSurface.focusEliminatePercent]
    );
    useEffect(
        () => setKeepBestText(String(Math.round(rawSurface.focusKeepBestPercent * 100))),
        [rawSurface.focusKeepBestPercent]
    );

    const commitIterations = () => {
        const error = iterationsText.length === 0 || Number(iterationsText) === 0;
        setIterationError(error);
        if (error) return;
        dispatch(setFocusIterations({ focusIterations: Number(iterationsText) }));
        commit();
    };
    const commitEliminate = () => {
        const error = eliminateText.length === 0 || Number(eliminateText) === 0;
        setEliminateError(error);
        if (error) return;
        dispatch(setFocusEliminatePercent({ focusEliminatePercent: Number(eliminateText) / 100 }));
        commit();
    };
    const commitKeepBest = () => {
        const error = keepBestText.length === 0 || Number(keepBestText) === 0;
        setKeepBestError(error);
        if (error) return;
        dispatch(setFocusKeepBestPercent({ focusKeepBestPercent: Number(keepBestText) / 100 }));
        commit();
    };

    const renderTraceTable = () => {
        if (!rawSurface.filterTrace) return null;
        const rows = ["machine", "motor"]
            .map((dataType) => ({
                dataType,
                filter: rawSurface.filterTrace.filters.find(
                    (f) => f.type === "FocusRemoveWorst" && f.data === dataType
                ),
            }))
            .filter((row) => row.filter);

        if (rows.length === 0) return null;

        return (
            <TableContainer>
                <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellSx} />
                            <TableCell sx={headerCellSx} />
                            <TableCell align="center" colSpan={4} sx={groupHeaderCellSx}>Removed</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={headerCellSx} />
                            <TableCell align="right" sx={headerCellSx}>Initial</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Y</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Z</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Total</TableCell>
                            <TableCell align="right" sx={headerCellSx}>%</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(({ dataType, filter }) => (
                            <TableRow key={dataType}>
                                <TableCell component="th" scope="row" sx={rowHeaderCellSx}>
                                    {capitalize(dataType)}
                                </TableCell>
                                <TableCell align="right" sx={numCellSx}>{filter.trace.initialpointcount}</TableCell>
                                <TableCell align="right" sx={numCellSx}>{filter.trace.removeY.removed}</TableCell>
                                <TableCell align="right" sx={numCellSx}>{filter.trace.removeZ.removed}</TableCell>
                                <TableCell align="right" sx={numCellSx}>
                                    {filter.trace.removeY.removed + filter.trace.removeZ.removed}
                                </TableCell>
                                <TableCell align="right" sx={numCellSx}>
                                    {calcPercent(filter.trace.removeZ.removed, filter.trace.removeY.removed, filter.trace.initialpointcount)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Card>
            <CardContent>
                <Typography sx={cardTitleSx}>Focus Remove Worst</Typography>
                <Typography variant="caption" color="text.secondary">
                    Iteratively drops the worst-fitting points per axis, refitting each pass, down
                    to the keep-best floor.
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5, mb: 1 }}>
                    <Typography variant="body2">Enabled</Typography>
                    <Switch
                        size="small"
                        checked={rawSurface.focusEnabled}
                        onChange={(e) => {
                            dispatch(setFocusEnabled({ enabled: e.target.checked }));
                            commit();
                        }}
                    />
                </Box>

                {rawSurface.focusEnabled && (
                    <>
                        <Box sx={{ display: "flex", gap: "8px", mb: "10px" }}>
                            <MiniField
                                label="Iterations" value={iterationsText}
                                onChange={(e) => setIterationsText(e.target.value)}
                                onBlur={commitIterations}
                                maxLength={3} error={iterationError}
                            />
                            <MiniField
                                label="Eliminate worst %" value={eliminateText}
                                onChange={(e) => setEliminateText(e.target.value)}
                                onBlur={commitEliminate}
                                maxLength={2} error={eliminateError}
                            />
                            <MiniField
                                label="Keep best %" value={keepBestText}
                                onChange={(e) => setKeepBestText(e.target.value)}
                                onBlur={commitKeepBest}
                                maxLength={2} error={keepBestError}
                            />
                        </Box>
                        {renderTraceTable()}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
